import {
  Component,
  computed,
  inject,
  input,
  output,
  OutputEmitterRef,
  Signal,
  signal,
  WritableSignal
} from '@angular/core';
import { FieldTree, FormField, FormRoot } from '@angular/forms/signals';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { ProjectAvatarImageComponent } from '@app/components/company/company-projects/company-project-basic-information/project-avatar-image/project-avatar-image.component';
import { CreateProjectModel } from '@app/components/company/company-projects/create-project-dialog/create-project-form-controls';
import { AvatarComponent } from '@app/shared/components/group-avatars/avatar/proffeo-avatar.component';
import { LabelComponent } from '@app/shared/components/label/label.component';
import { ActiveCompanyService } from '@app/shared/services/active-company.service';
import { FileDataService } from '@app/shared/services/file-data.service';
import { ProjectsDataService } from '@app/shared/services/projects-data.service';
import { DialogService } from '@app/shared/services/shared/dialog.service';
import { AvatarSize } from '@app/shared/types/enums/avatar-size.enum';
import { Currency } from '@app/shared/types/enums/currency.enum';
import { FileContext } from '@app/shared/types/enums/file-enums';
import { TimeUnits } from '@app/shared/types/enums/time-units.enum';
import { Company } from '@app/shared/types/models/company/company.model';
import { UploadData } from '@app/shared/types/models/files/upload-data.model';
import { Project } from '@app/shared/types/models/project/project.model';
import { DropdownItem } from '@app/shared/types/models/shared/dropdown-item';
import { User } from '@app/shared/types/models/user/user.model';
import { UiDatePickerComponent } from '@app/shared/ui/date-picker/ui-date-picker.component';
import { UiInputComponent } from '@app/shared/ui/input/ui-input.component';
import { UiSelectComponent } from '@app/shared/ui/select/ui-select.component';
import { UiTextareaComponent } from '@app/shared/ui/textarea/ui-textarea.component';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'proffeo-project-basic-information',
  imports: [
    TranslatePipe,
    FormField,
    FormRoot,
    MatAutocompleteModule,
    UiDatePickerComponent,
    UiInputComponent,
    UiSelectComponent,
    AvatarComponent,
    UiTextareaComponent,
    ProjectAvatarImageComponent,
    LabelComponent
  ],
  templateUrl: './project-basic-information.component.html'
})
export class ProjectBasicInformationComponent {
  private readonly translateService: TranslateService = inject(TranslateService);
  private readonly dialogService: DialogService = inject(DialogService);
  private readonly fileService: FileDataService = inject(FileDataService);
  private readonly projectService: ProjectsDataService = inject(ProjectsDataService);
  private readonly activeCompanyService: ActiveCompanyService = inject(ActiveCompanyService);
  private readonly nameValue: Signal<string> = computed(() => this.form().name().value() ?? '');
  private selectedSupervisor: WritableSignal<User> = signal(undefined);
  /** Identifies the latest avatar upload; an older upload finishing later must not overwrite the avatar. */
  private latestAvatarUpload: object | undefined;

  protected readonly AvatarSize = AvatarSize;
  protected readonly Currencies = Object.values(Currency);
  protected readonly currencySelectOptions: DropdownItem<Currency>[] = this.Currencies.map(currency => ({
    value: currency,
    label: currency
  }));
  protected readonly filteredDraftProjects: Signal<Project[] | undefined> = computed(() => {
    return this.isAddProjectModal()
      ? this.projectService
          .getCompanyDraftProjects(this.activeCompany()?.id)()
          .filter(project => project.name.toLowerCase().includes(this.nameValue().trim().toLowerCase()))
      : undefined;
  });
  protected goToEditDraftCompanyProject: OutputEmitterRef<Project> = output<Project>();
  protected activeCompany: Signal<Company | undefined> = this.activeCompanyService.activeCompany;

  /** Project form of the create or edit dialog (the edit model only adds `id`). */
  public readonly form = input.required<FieldTree<CreateProjectModel>>();
  public readonly isAddProjectModal = input<boolean>(false);

  protected get timeUnitsItems(): DropdownItem<string>[] {
    return [
      {
        label: this.translateService.instant('options-project-man-days'),
        value: TimeUnits.MAN_DAYS
      },
      {
        label: this.translateService.instant('options-project-hours'),
        value: TimeUnits.HOURS
      }
    ];
  }

  protected openSelectProjectSupervisorDialog(): void {
    this.dialogService
      .openSelectProjectSupervisorDialog({
        preselectedUser: this.form().supervisor().value(),
        selectedUser: this.selectedSupervisor
      })
      .afterClosed()
      .subscribe((selectedUser: User | null) => {
        if (selectedUser) {
          this.form().supervisor().value.set(selectedUser);
        }
      });
  }

  protected saveImage(image: File): void {
    const uploadData: UploadData = {
      description: null,
      file: image,
      fileTags: [],
      orderNo: 0
    };
    const upload = {};
    this.latestAvatarUpload = upload;
    this.fileService.uploadFile(
      {
        context: FileContext.projectDraftAvatar,
        projectId: crypto.randomUUID()
      },
      uploadData,
      file => {
        if (this.latestAvatarUpload === upload) {
          this.form().avatarId().value.set(file.id);
        }
      }
    );
  }

  protected onExistingDraftProjectSelected(event: MatAutocompleteSelectedEvent): void {
    const selectedDraftProject: Project = event.option.value;
    this.goToEditDraftCompanyProject.emit(selectedDraftProject);
  }
}
